/* Parse an uploaded RFP workbook and extract questions.
 *
 * Finds the header row, question column, and answer column per sheet, then
 * yields one {sheet, row, col, question, context} per requirement. The
 * `row`/`col` coordinates are 0-indexed and get carried through realFill so we
 * can write answers back into the user's original workbook on export.
 *
 * Column detection is SCORED, not first-match-wins. The old first-match loop
 * picked "Requirement ID" over "Requirement Description" on any RTM whose first
 * column is an ID, so the model received a bare ID string as the question.
 */

// Default import, not `import * as`: the package is CJS, and only the default
// form resolves to the module body under both Vite and raw Node ESM (which the
// parser tests use).
import XLSX from 'xlsx-js-style';

const HEADER_KW = /module|requirement|feature|can provide|y\s*\/?\s*n|\bmet\b|not met|response|comment|answer|capability|description|question/i;
const COMMENT_KW = /comment|description|how|explain|detail|note/i;

// Question-column keywords. STRONG names the requirement text itself; the
// weaker set also matches columns that merely live near it.
const QUESTION_KW = /requirement|question|description|feature|capability|spec/i;
const STRONG_QUESTION_KW = /description|question|future.?state|requirement text|capability/i;
// ID-shaped headers can never be the question. Note `#` is not a word
// character, so it is matched outside a \b group.
const ID_KW = /(\bid\b|#|\bno\.?\b|\bnum(ber)?\b|\bref\b|\bcode\b|\bitem\b)/i;

// Answer-column keywords. STRONG names where the vendor writes prose; WEAK
// matches status/scoring columns, which must lose to a real response column.
const STRONG_ANSWER_KW = /vendor response|\bresponse\b|\banswer\b|can provide/i;
const WEAK_ANSWER_KW = /\bmet\b|not met|partially|\bvendor\b|y\s*\/?\s*n/i;
// A header enumerating several statuses is a scoring column, not a response
// column — e.g. "Requirement (Met, Partially Met, Not Met)".
const STATUS_HINT = /\bmet\b[\s\S]*not met|partially met|\bmet\s*\/\s*not/i;

// Sheets that are reference material, not questionnaires.
const NON_QUESTION_SHEET = /glossary|definitions?|instructions?|cover|legend|read ?me|\btoc\b|table of contents|revision|change log/i;

// Row ceiling per sheet. The old value was 500, which silently truncated longer
// RTMs; when this bites we now flag it instead of dropping rows quietly.
const MAX_ROWS = 5000;

function cellAt(ws, r, c) {
  const addr = XLSX.utils.encode_cell({ r, c });
  return ws[addr];
}

function textOf(cell) {
  if (!cell) return '';
  const v = cell.v;
  if (v == null) return '';
  return String(v).trim();
}

/** Mean length of the non-empty cells in a column range. */
function avgLen(ws, col, from, to) {
  let total = 0, n = 0;
  for (let r = from; r <= to; r++) {
    const t = textOf(cellAt(ws, r, col));
    if (t) { total += t.length; n++; }
  }
  return n ? total / n : 0;
}

/** Count of non-empty cells in a column range. */
function countNonEmpty(ws, col, from, to) {
  let n = 0;
  for (let r = from; r <= to; r++) {
    if (textOf(cellAt(ws, r, col))) n++;
  }
  return n;
}

function detectSheet(ws, sheetName) {
  if (!ws || !ws['!ref']) return null;
  const range = XLSX.utils.decode_range(ws['!ref']);
  const maxR = Math.min(range.e.r, MAX_ROWS);
  const truncated = range.e.r > MAX_ROWS;
  const maxC = range.e.c;

  // Header row: first row where ≥2 cells match header keywords OR look like headers.
  let headerRow = 0, headerScore = 0;
  const headerScanLimit = Math.min(maxR, 10);
  for (let r = 0; r <= headerScanLimit; r++) {
    let score = 0;
    for (let c = 0; c <= maxC; c++) {
      const t = textOf(cellAt(ws, r, c));
      if (t && HEADER_KW.test(t)) score++;
    }
    if (score > headerScore) { headerScore = score; headerRow = r; }
  }
  if (headerScore === 0) headerRow = 0;

  const headers = {};
  for (let c = 0; c <= maxC; c++) {
    headers[c] = textOf(cellAt(ws, headerRow, c));
  }

  const scanEnd = Math.min(headerRow + 60, maxR);
  const scanRows = Math.max(1, scanEnd - headerRow);

  // ---- Question column: score every candidate, highest wins. ----
  let questionCol = -1, bestQ = -Infinity;
  for (let c = 0; c <= maxC; c++) {
    const h = headers[c] || '';
    const avg = avgLen(ws, c, headerRow + 1, scanEnd);
    // Too short to be requirement text (IDs, codes, Y/N, category labels).
    if (avg < 12) continue;
    // Too sparse to be the question column — catches a lone instruction cell
    // sitting in an otherwise empty column.
    const fill = countNonEmpty(ws, c, headerRow + 1, scanEnd) / scanRows;
    if (fill < 0.4) continue;

    let score = avg;
    if (STRONG_QUESTION_KW.test(h)) score += 120;
    else if (QUESTION_KW.test(h)) score += 40;
    if (ID_KW.test(h)) score -= 200;

    if (score > bestQ) { bestQ = score; questionCol = c; }
  }
  if (questionCol < 0) questionCol = 0;

  // ---- Answer column: score too. A real response column must beat a status
  // column whose header merely contains the word "Met". ----
  let answerCol = -1, bestA = -Infinity, answerIsStrong = false;
  for (let c = 0; c <= maxC; c++) {
    if (c === questionCol) continue;
    const h = headers[c] || '';
    const emptyRatio = 1 - countNonEmpty(ws, c, headerRow + 1, scanEnd) / scanRows;

    let score = emptyRatio * 60;
    const strong = STRONG_ANSWER_KW.test(h);
    if (strong) score += 120;
    else if (WEAK_ANSWER_KW.test(h)) score += 30;
    else score -= 40;
    if (STATUS_HINT.test(h)) score -= 100;

    if (score > bestA) { bestA = score; answerCol = c; answerIsStrong = strong; }
  }
  if (answerCol < 0) answerCol = questionCol + 1;

  // Guardrail: never overwrite a column the client already filled in. Append a
  // fresh column instead and tell the caller we did.
  let appendedAnswerCol = false;
  const answerPopulated = countNonEmpty(ws, answerCol, headerRow + 1, scanEnd) / scanRows;
  if (answerPopulated > 0.2) {
    answerCol = maxC + 1;
    appendedAnswerCol = true;
  }

  // Comment column (optional).
  let commentCol = -1;
  for (let c = 0; c <= maxC; c++) {
    if (c === questionCol || c === answerCol) continue;
    if (COMMENT_KW.test(headers[c])) { commentCol = c; break; }
  }

  // Guardrail: is this a questionnaire at all?
  const questionAvg = avgLen(ws, questionCol, headerRow + 1, scanEnd);
  let skipReason = null;
  if (NON_QUESTION_SHEET.test(sheetName || '')) {
    skipReason = 'sheet name looks like reference material, not questions';
  } else if (questionAvg < 25 && !answerIsStrong) {
    skipReason = `question column averages ${Math.round(questionAvg)} chars — too short to be requirement text`;
  }

  // Collect questions — skip rows where the question cell is empty or short.
  const questions = [];
  for (let r = headerRow + 1; r <= maxR; r++) {
    const cell = cellAt(ws, r, questionCol);
    const t = textOf(cell);
    if (!t || t.length < 3) continue;
    // Skip bold section header rows (heuristic — they're often labels, not questions).
    const isBold = !!(cell?.s?.font?.bold);
    if (isBold && t.length < 40) continue;

    // Row context: every other populated cell on this row, labelled by its
    // header. This is what makes an answer specific rather than generic — the
    // business rationale, current-state process and future-state capability all
    // live in sibling columns.
    const context = [];
    for (let c = 0; c <= maxC; c++) {
      if (c === questionCol || c === answerCol) continue;
      const h = headers[c];
      if (!h) continue;
      let v = textOf(cellAt(ws, r, c));
      if (!v) continue;
      if (v.length > 300) v = v.slice(0, 300) + '…';
      context.push(`${h}: ${v}`);
    }

    questions.push({ row: r, col: questionCol, question: t, context });
  }

  return {
    headerRow, questionCol, answerCol, commentCol, questions,
    appendedAnswerCol, truncated, skipReason,
  };
}

/**
 * Parse an uploaded file.
 *
 * Returns:
 *   { workbook, questions, detection, skippedSheets }
 *
 * - workbook: XLSX.WorkBook preserving original formatting, for write-back on export.
 * - questions: [{ id, sheet, row, col, question, context }, ...] across all sheets.
 * - detection: { [sheetName]: { headerRow, questionCol, answerCol, commentCol,
 *                               appendedAnswerCol, truncated } }
 * - skippedSheets: [{ sheet, reason, sampleQuestionCount }] — detected but not
 *   answered, so the UI can show them rather than dropping them silently.
 */
export async function parseRfpWorkbook(file) {
  const buf = await file.arrayBuffer();
  const workbook = XLSX.read(buf, { type: 'array', cellStyles: true });

  const questions = [];
  const detection = {};
  const skippedSheets = [];
  let id = 0;

  for (const name of workbook.SheetNames) {
    const ws = workbook.Sheets[name];
    const det = detectSheet(ws, name);
    if (!det) continue;

    if (!det.questions.length) {
      skippedSheets.push({ sheet: name, reason: 'no question-shaped rows found', sampleQuestionCount: 0 });
      continue;
    }
    if (det.skipReason) {
      skippedSheets.push({ sheet: name, reason: det.skipReason, sampleQuestionCount: det.questions.length });
      continue;
    }

    detection[name] = {
      headerRow: det.headerRow,
      questionCol: det.questionCol,
      answerCol: det.answerCol,
      commentCol: det.commentCol >= 0 ? det.commentCol : undefined,
      appendedAnswerCol: det.appendedAnswerCol || undefined,
      truncated: det.truncated || undefined,
    };
    for (const q of det.questions) {
      questions.push({
        id: id++,
        sheet: name,
        row: q.row,
        col: q.col,
        question: q.question,
        context: q.context,
      });
    }
  }

  return { workbook, questions, detection, skippedSheets };
}
